// Nutrition sheet support for the bulk upload (see docs/superpowers/specs/2026-08-14-bulk-upload-remove-fields-design.md).
// CommonJS so the plain-Node unit tests can require it without a bundler.
//
// Blank nutrient cells must keep the stored value instead of restating the whole table, so the
// bulk upload only ever collects what a row actually provides (buildNutrientUpdates) and a
// separate post-merge step (applyNutrientUpdates) writes those provided values into the stored
// table. The default tables and the sheet-column map below are moved verbatim from
// BulkUpload.vue's processNutritionSheet, which used to build a full table on every row.

const PENDING_NUTRIENTS_KEY = '__st_pending_nutrients__'

// The energy row's kcal line is identified by tableKey === null (see SHEET_KEY_TO_NUTRIENT
// below), and an object can't have null as a key, so this name stands in for it.
const KCAL_SET_KEY = '__kcal__'

const ALCOHOL_NUTRITION_TABLE = [
  { key: 'energy', per_100ml: 0, per_portion: null, unit: 'kJ', less_operator: false, children: [{ key: null, per_100ml: 0, per_portion: null, unit: 'kcal', less_operator: false }] },
  { key: 'fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false, children: [{ key: 'saturated_fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }] },
  { key: 'carbohydrate', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false, children: [{ key: 'sugar', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }] },
  { key: 'protein', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false },
  { key: 'salt', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }
]

const FOOD_BEVERAGE_NUTRITION_TABLE = [
  { key: 'energy', per_100ml: 0, per_portion: null, unit: 'kJ', less_operator: false, children: [{ key: null, per_100ml: 0, per_portion: null, unit: 'kcal', less_operator: false }] },
  { key: 'fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false, children: [{ key: 'saturated_fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }, { key: 'monounsaturated_fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }, { key: 'polyunsaturated_fat', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }] },
  { key: 'carbohydrate', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false, children: [{ key: 'sugar', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }, { key: 'polyols', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }, { key: 'starch', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }] },
  { key: 'fibre', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false },
  { key: 'protein', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false },
  { key: 'salt', per_100ml: 0, per_portion: null, unit: 'g', less_operator: false }
]

const SHEET_KEY_TO_NUTRIENT = {
  energy_kj: 'energy',
  energy_kcal: null,
  fat_g: 'fat',
  saturates_g: 'saturated_fat',
  mono_unsaturates_g: 'monounsaturated_fat',
  polyunsaturates_g: 'polyunsaturated_fat',
  carbohydrate_g: 'carbohydrate',
  sugar_g: 'sugar',
  polyols_g: 'polyols',
  starch_g: 'starch',
  fibre_g: 'fibre',
  protein_g: 'protein',
  salt_g: 'salt',
  // Optional nutrients -----------------
  biotin_μg: 'biotin',
  calcium_mg: 'calcium',
  chloride_mg: 'chloride',
  chromium_μg: 'chromium',
  copper_mg: 'copper',
  fluoride_mg: 'fluoride',
  folic_acid_μg: 'folic_acid',
  iodine_μg: 'iodine',
  iron_mg: 'iron',
  magnesium_mg: 'magnesium',
  manganese_mg: 'manganese',
  molybdenum_μg: 'molybdenum',
  niacin_mg: 'niacin',
  pantothenic_acid_mg: 'pantothenic_acid',
  phosphorus_mg: 'phosphorus',
  potassium_mg: 'potassium',
  riboflavin_mg: 'riboflavin',
  selenium_μg: 'selenium',
  thiamin_mg: 'thiamin',
  vitamin_a_μg: 'vitamin_a',
  vitamin_b12_μg: 'vitamin_b12',
  vitamin_b6_mg: 'vitamin_b6',
  vitamin_c_mg: 'vitamin_c',
  vitamin_d_μg: 'vitamin_d',
  vitamin_e_mg: 'vitamin_e',
  vitamin_k_μg: 'vitamin_k',
  zinc_mg: 'zinc'
}

function parseNutrientValue (raw) {
  if (typeof raw === 'number') return isNaN(raw) ? null : { value: raw, lessOperator: false }
  if (typeof raw !== 'string') return null
  let trimmed = raw.trim()
  if (!trimmed) return null
  let lessOperator = false
  if (trimmed.indexOf('<') === 0) {
    lessOperator = true
    trimmed = trimmed.slice(1).trim()
  }
  const value = parseFloat(trimmed.replace(',', '.'))
  return isNaN(value) ? null : { value, lessOperator }
}

// Walks every nutrient column of one sheet row. It never decides whether a #REMOVE is
// actually allowed - applyNutrientUpdates does that, because it's the only side that knows
// the product's template type and whether the nutrient is an optional one.
function buildNutrientUpdates (entry, { isRemoveKeyword }) {
  const set = {}
  const remove = []
  let kcalProvided = false

  Object.entries(SHEET_KEY_TO_NUTRIENT).forEach(([sheetKey, tableKey]) => {
    const rawValue = entry[sheetKey]
    if (isRemoveKeyword(rawValue)) {
      // kcal can never be removed, so drop the request here instead of passing along a
      // meaningless null for applyNutrientUpdates to ignore later.
      if (tableKey !== null) remove.push(tableKey)
      return
    }
    const parsed = parseNutrientValue(rawValue)
    if (!parsed) return
    if (tableKey === null) {
      set[KCAL_SET_KEY] = parsed
      kcalProvided = true
    } else {
      set[tableKey] = parsed
    }
  })

  return { set, remove, kcalProvided }
}

function findEnergyChild (table) {
  const energyRow = table.find((row) => row.key === 'energy')
  return energyRow && energyRow.children ? energyRow.children.find((child) => child.key === null) : null
}

function findNutrientRow (table, tableKey) {
  if (tableKey === null) return findEnergyChild(table)
  for (const row of table) {
    if (row.key === tableKey) return row
    if (row.children) {
      const child = row.children.find((childRow) => childRow.key === tableKey)
      if (child) return child
    }
  }
  return null
}

// A plain number always clears a stored "<": it only survives while the cell keeps sending
// "<" on every upload, so a number on its own means "this is now the exact value".
function writeNutrientValue (row, entryValue) {
  row.per_100ml = entryValue.value
  row.per_portion = null
  row.less_operator = entryValue.lessOperator
}

function applyNutrientValues (table, setEntries, { isFoodAndBeverage, nutritionInfoList }) {
  Object.entries(setEntries).forEach(([setKey, entryValue]) => {
    const tableKey = setKey === KCAL_SET_KEY ? null : setKey
    const row = findNutrientRow(table, tableKey) || appendOptionalNutrient(table, tableKey, { isFoodAndBeverage, nutritionInfoList })
    if (row) writeNutrientValue(row, entryValue)
  })
}

// Only Food & Beverage products can gain new nutrient rows (alcohol templates have a fixed
// list), and only for nutrients marked optional. Without that check, a nutrient that belongs
// underneath another one (polyunsaturated_fat sits under fat) could be added as a row of its
// own instead - wrong on a regulated label.
function appendOptionalNutrient (table, tableKey, { isFoodAndBeverage, nutritionInfoList }) {
  if (!isFoodAndBeverage || tableKey === null) return null
  const nutrientInfo = nutritionInfoList.find((info) => info.key === tableKey)
  if (!nutrientInfo || !nutrientInfo.is_optional) return null
  const newRow = { key: tableKey, per_100ml: 0, per_portion: null, unit: nutrientInfo.unit || 'g', less_operator: false }
  table.push(newRow)
  return newRow
}

// Every nutrient the given standard table declares, both the main rows and the ones nested
// under them (the kcal line is skipped - it's only ever reached through the __kcal__ name).
function collectDefaultTableKeys (defaultTable) {
  const keys = new Set()
  defaultTable.forEach((row) => {
    if (row.key !== null) keys.add(row.key)
    if (row.children) {
      row.children.forEach((child) => {
        if (child.key !== null) keys.add(child.key)
      })
    }
  })
  return keys
}

// A nutrient can be removed when it's optional, or when it doesn't belong to this product
// type's own standard table at all - like a fibre row still sitting on a wine product after
// someone switched its template type, which is left-over data rather than real structure.
// The EU-required nutrients (fat, salt, ...) are in both standard tables, so they're always
// protected.
function isNutrientRemovable (tableKey, { isFoodAndBeverage, nutritionInfoList }) {
  if (tableKey === null) return false
  const nutrientInfo = nutritionInfoList.find((info) => info.key === tableKey)
  if (nutrientInfo && nutrientInfo.is_optional) return true
  const defaultTable = isFoodAndBeverage ? FOOD_BEVERAGE_NUTRITION_TABLE : ALCOHOL_NUTRITION_TABLE
  return !collectDefaultTableKeys(defaultTable).has(tableKey)
}

// Whether a removal is allowed is decided here, not while reading the sheet, because only
// this side knows the product's template type. Left-over nutrients from a different type can
// be stored nested under another nutrient rather than on their own row (monounsaturated_fat
// under fat, polyols under carbohydrate), so both places are searched.
function applyNutrientRemovals (table, removeKeys, nutritionInfoList, isFoodAndBeverage) {
  removeKeys.forEach((tableKey) => {
    if (!isNutrientRemovable(tableKey, { isFoodAndBeverage, nutritionInfoList })) return
    const rowIndex = table.findIndex((row) => row.key === tableKey)
    if (rowIndex !== -1) {
      table.splice(rowIndex, 1)
      return
    }
    for (const row of table) {
      if (!row.children) continue
      const childIndex = row.children.findIndex((child) => child.key === tableKey)
      if (childIndex !== -1) {
        row.children.splice(childIndex, 1)
        return
      }
    }
  })
}

function applyKcalAutoCalc (table, setEntries, kcalProvided) {
  if (kcalProvided || !setEntries.energy) return
  const kcalChild = findEnergyChild(table)
  if (!kcalChild) return
  kcalChild.per_100ml = Math.round(setEntries.energy.value * 0.239006)
}

// Gives every product its own copy of the standard table rather than sharing one array, and
// only when there isn't a table already. A template can ship a table while missing some of
// the settings below (wine.json has a table but no showZeroAsNegligible), so those are filled
// in every time this runs, not only when a table gets created - otherwise those templates
// would never get them. Each one only fills in when it's still undefined, so anything the
// template or the sheet already set is left alone.
function initializeNutritionTable (nutrition, { isFoodAndBeverage, isAlcoholType }) {
  if (!Array.isArray(nutrition.table) || nutrition.table.length === 0) {
    const defaultTable = isAlcoholType ? ALCOHOL_NUTRITION_TABLE : FOOD_BEVERAGE_NUTRITION_TABLE
    nutrition.table = JSON.parse(JSON.stringify(defaultTable))
  }

  // Both templates' old from-scratch init carried this toggle - not just Food & Beverage.
  if (nutrition.showZeroAsNegligible === undefined) nutrition.showZeroAsNegligible = false

  if (isFoodAndBeverage) {
    if (nutrition.mode === undefined) nutrition.mode = 'table'
    if (nutrition.is_hidden === undefined) nutrition.is_hidden = false
    if (nutrition.custom_icons === undefined) nutrition.custom_icons = []
    if (nutrition.display_reference_intake === undefined) nutrition.display_reference_intake = false
  }
}

function applyNutrientUpdates (elabelObject, { isFoodAndBeverage, isAlcoholType, nutritionInfoList }) {
  const updates = elabelObject.nutrition && elabelObject.nutrition[PENDING_NUTRIENTS_KEY]
  if (!updates) return elabelObject

  // Utils.mergeDeep throws empty arrays away, so a row that only set values arrives here with
  // no `remove` list at all. Default both halves before reading them - this has to cope with
  // whatever shape comes out of the merge, not just the one built today.
  const removeKeys = Array.isArray(updates.remove) ? updates.remove : []
  const setEntries = updates.set || {}

  // A row that only touched the settings (or had nothing readable in it) leaves nothing to
  // apply. Stop before initializeNutritionTable, or a product with no nutrition data at all
  // would suddenly gain a full table of zeros.
  if (Object.keys(setEntries).length === 0 && removeKeys.length === 0) {
    delete elabelObject.nutrition[PENDING_NUTRIENTS_KEY]
    return elabelObject
  }

  initializeNutritionTable(elabelObject.nutrition, { isFoodAndBeverage, isAlcoholType })
  const table = elabelObject.nutrition.table

  applyNutrientValues(table, setEntries, { isFoodAndBeverage, nutritionInfoList })
  applyNutrientRemovals(table, removeKeys, nutritionInfoList, isFoodAndBeverage)
  applyKcalAutoCalc(table, setEntries, updates.kcalProvided)

  delete elabelObject.nutrition[PENDING_NUTRIENTS_KEY]
  return elabelObject
}

module.exports = {
  PENDING_NUTRIENTS_KEY,
  ALCOHOL_NUTRITION_TABLE,
  FOOD_BEVERAGE_NUTRITION_TABLE,
  SHEET_KEY_TO_NUTRIENT,
  buildNutrientUpdates,
  applyNutrientUpdates,
  isNutrientRemovable
}
